
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import {
  UserPlus, Users, Mail, ShieldCheck, KeyRound, ArrowLeft,
  Eye, EyeOff, Save, X, ChevronRight, UserRound,
  LockKeyhole, CheckCircle2, Loader2
} from "lucide-react";

// ============================================================
// API CONFIGURATION
// ============================================================

const API = (process.env.REACT_APP_API_URL || "https://supremeadmin.nissi-india.com").replace(/\/$/, "");

const api = axios.create({
  baseURL: `${API}/api`,
  withCredentials: true,
  timeout: 30000
});

const LIST_ROUTE = "/supremeadmin/supremeAdminUsers_List";
const ROLES = [
  { value: "SUPREME_ADMIN", label: "Supreme Admin" },
  { value: "AUDITOR", label: "Auditor" }
];

const initialForm = {
  username: "",
  display_name: "",
  email: "",
  role: "SUPREME_ADMIN",
  is_active: "1",
  password: "",
  confirm: ""
};

const inputClass = "w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10 disabled:bg-slate-50 disabled:text-slate-400";

const labelClass = "mb-1.5 block text-xs font-semibold text-slate-700";

const getError = (error) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.message ||
  "Something went wrong";

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function SuperAdminUsers_Add() {
  const navigate = useNavigate();

  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (field) => (event) => {
    const value = event.target.value;

    setForm((previous) => ({
      ...previous,
      [field]: value
    }));

    setErrors((previous) => ({
      ...previous,
      [field]: ""
    }));
  };

  // ============================================================
  // FORM VALIDATION
  // ============================================================

  const validate = () => {
    const nextErrors = {};
    const username = form.username.trim();
    const email = form.email.trim();
    const displayName = form.display_name.trim();

    if (!username) {
      nextErrors.username = "Username is required";
    } else if (!/^[A-Za-z0-9_.-]{3,100}$/.test(username)) {
      nextErrors.username = "Username must be 3-100 characters (letters, numbers, _, . or -)";
    }

    if (displayName.length > 200) {
      nextErrors.display_name = "Display name cannot exceed 200 characters";
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = "Enter a valid email address";
    } else if (email.length > 255) {
      nextErrors.email = "Email cannot exceed 255 characters";
    }

    if (!ROLES.some((item) => item.value === form.role)) {
      nextErrors.role = "Select a valid role";
    }

    if (!["1", "0"].includes(form.is_active)) {
      nextErrors.is_active = "Select a valid status";
    }

    if (!form.password) {
      nextErrors.password = "Password is required";
    } else if (form.password.length < 8) {
      nextErrors.password = "Password must contain at least 8 characters";
    } else if (new TextEncoder().encode(form.password).length > 1024) {
      nextErrors.password = "Password is too long";
    }

    if (!form.confirm) {
      nextErrors.confirm = "Confirm your password";
    } else if (form.password !== form.confirm) {
      nextErrors.confirm = "Passwords do not match";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // ============================================================
  // SAVE SUPREME ADMIN
  // ============================================================

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (saving || !validate()) return;

    setSaving(true);

    try {
      const payload = {
        username: form.username.trim(),
        display_name: form.display_name.trim() || null,
        email: form.email.trim().toLowerCase() || null,
        role: form.role,
        is_active: Number(form.is_active),
        password: form.password
      };

      const response = await api.post("/superadmin/users", payload);

      if (response.data?.ok === false || response.data?.success === false) {
        throw new Error(response.data.error || "Failed to create Supreme Admin");
      }

      await Swal.fire({
        icon: "success",
        title: "Supreme Admin Created",
        text: response.data?.message || "Supreme Admin user created successfully",
        confirmButtonColor: "#047857",
        confirmButtonText: "OK"
      });

      navigate(LIST_ROUTE);
    } catch (error) {
      console.error("Create Supreme Admin error:", error);

      await Swal.fire({
        icon: "error",
        title: "Unable to Save",
        text: getError(error),
        confirmButtonColor: "#047857"
      });
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // RESET FORM
  // ============================================================

  const resetForm = async () => {
    if (saving) return;

    const result = await Swal.fire({
      icon: "question",
      title: "Clear Form?",
      text: "All entered details will be cleared.",
      showCancelButton: true,
      confirmButtonText: "Yes, Clear",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#047857"
    });

    if (result.isConfirmed) {
      setForm({ ...initialForm });
      setErrors({});
      setShowPwd(false);
      setShowConfirm(false);
    }
  };

  // ============================================================
  // FIELD COMPONENT
  // ============================================================

  const FieldError = ({ name }) =>
    errors[name] ? (
      <p className="mt-1 text-[11px] font-medium text-red-600">
        {errors[name]}
      </p>
    ) : null;

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="min-h-screen bg-[#f5f8f6] text-slate-800">
      <div className="flex min-h-screen flex-col md:h-screen md:flex-row md:overflow-hidden">
        <SuperAdminNav />

        <main className="min-w-0 w-full flex-1 overflow-auto px-4 py-5 md:px-6 lg:px-8">
          <div className="mx-auto max-w-[1450px]">

            {/* BREADCRUMB */}
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                <Link to={LIST_ROUTE} className="hover:text-emerald-700">
                  Supreme Admin Users
                </Link>
                <ChevronRight size={13} />
                <span className="font-bold text-emerald-700">
                  Add Supreme Admin
                </span>
              </div>
              <Time />
            </div>

            {/* PAGE HEADER */}
            <div className="mb-5 overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4 border-l-4 border-emerald-600 px-5 py-5">
                <div className="flex items-start gap-3">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700">
                    <UserPlus size={24} />
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                      Infra V5 / Administration
                    </p>
                    <h1 className="text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
                      Add Supreme Admin
                    </h1>
                    <p className="mt-1 text-xs text-slate-500">
                      Create a new Supreme Admin or Auditor account.
                    </p>
                  </div>
                </div>

                <Link
                  to={LIST_ROUTE}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  <ArrowLeft size={15} />
                  Back to List
                </Link>
              </div>
            </div>

            {/* FORM */}
            <form onSubmit={handleSubmit} noValidate>
              <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">

                {/* SECTION HEADER */}
                <div className="flex items-center gap-3 border-b border-emerald-100 bg-emerald-50/60 px-5 py-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <Users size={18} />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-800">
                      Supreme Admin Details
                    </h2>
                    <p className="mt-0.5 text-[11px] text-slate-500">
                      Enter account information, access role and login credentials.
                    </p>
                  </div>
                </div>

                <div className="p-5 md:p-6">

                  {/* ACCOUNT DETAILS */}
                  <div className="mb-5 flex items-center gap-2 border-b border-slate-100 pb-3">
                    <UserRound size={16} className="text-emerald-700" />
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-700">
                      Account Information
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">

                    {/* USERNAME */}
                    <div>
                      <label htmlFor="username" className={labelClass}>
                        Username <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="username"
                        name="username"
                        type="text"
                        autoComplete="off"
                        maxLength={100}
                        value={form.username}
                        onChange={onChange("username")}
                        placeholder="Enter username"
                        disabled={saving}
                        className={`${inputClass} ${errors.username ? "border-red-400" : ""}`}
                      />
                      <FieldError name="username" />
                    </div>

                    {/* DISPLAY NAME */}
                    <div>
                      <label htmlFor="display_name" className={labelClass}>
                        Display Name
                      </label>
                      <input
                        id="display_name"
                        name="display_name"
                        type="text"
                        maxLength={200}
                        value={form.display_name}
                        onChange={onChange("display_name")}
                        placeholder="Enter display name"
                        disabled={saving}
                        className={inputClass}
                      />
                      <FieldError name="display_name" />
                    </div>

                    {/* EMAIL */}
                    <div>
                      <label htmlFor="email" className={labelClass}>
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail size={15} className="pointer-events-none absolute left-3 top-3 text-slate-400" />
                        <input
                          id="email"
                          name="email"
                          type="email"
                          maxLength={255}
                          autoComplete="off"
                          value={form.email}
                          onChange={onChange("email")}
                          placeholder="admin@example.com"
                          disabled={saving}
                          className={`${inputClass} pl-9 ${errors.email ? "border-red-400" : ""}`}
                        />
                      </div>
                      <FieldError name="email" />
                    </div>

                    {/* ROLE */}
                    <div>
                      <label htmlFor="role" className={labelClass}>
                        Role <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <ShieldCheck size={15} className="pointer-events-none absolute left-3 top-3 text-slate-400" />
                        <select
                          id="role"
                          name="role"
                          value={form.role}
                          onChange={onChange("role")}
                          disabled={saving}
                          className={`${inputClass} pl-9`}
                        >
                          {ROLES.map((item) => (
                            <option key={item.value} value={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <FieldError name="role" />
                    </div>

                    {/* STATUS */}
                    <div>
                      <label htmlFor="is_active" className={labelClass}>
                        Account Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="is_active"
                        name="is_active"
                        value={form.is_active}
                        onChange={onChange("is_active")}
                        disabled={saving}
                        className={inputClass}
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                      <FieldError name="is_active" />
                    </div>
                  </div>

                  {/* PASSWORD SECTION */}
                  <div className="mb-5 mt-8 flex items-center gap-2 border-b border-slate-100 pb-3">
                    <LockKeyhole size={16} className="text-emerald-700" />
                    <h3 className="text-xs font-bold uppercase tracking-wide text-slate-700">
                      Login Credentials
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2">

                    {/* PASSWORD */}
                    <div>
                      <label htmlFor="password" className={labelClass}>
                        Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <KeyRound size={15} className="pointer-events-none absolute left-3 top-3 text-slate-400" />
                        <input
                          id="password"
                          name="password"
                          type={showPwd ? "text" : "password"}
                          autoComplete="new-password"
                          value={form.password}
                          onChange={onChange("password")}
                          placeholder="Enter password"
                          disabled={saving}
                          className={`${inputClass} pl-9 pr-10 ${errors.password ? "border-red-400" : ""}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPwd((previous) => !previous)}
                          className="absolute right-3 top-3 text-slate-400 transition hover:text-emerald-700"
                          aria-label={showPwd ? "Hide password" : "Show password"}
                        >
                          {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <FieldError name="password" />
                      <p className="mt-1 text-[11px] text-slate-400">
                        Minimum 8 characters. Use a strong, unique password.
                      </p>
                    </div>

                    {/* CONFIRM PASSWORD */}
                    <div>
                      <label htmlFor="confirm" className={labelClass}>
                        Confirm Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <KeyRound size={15} className="pointer-events-none absolute left-3 top-3 text-slate-400" />
                        <input
                          id="confirm"
                          name="confirm"
                          type={showConfirm ? "text" : "password"}
                          autoComplete="new-password"
                          value={form.confirm}
                          onChange={onChange("confirm")}
                          placeholder="Re-enter password"
                          disabled={saving}
                          className={`${inputClass} pl-9 pr-10 ${errors.confirm ? "border-red-400" : ""}`}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm((previous) => !previous)}
                          className="absolute right-3 top-3 text-slate-400 transition hover:text-emerald-700"
                          aria-label={showConfirm ? "Hide password" : "Show password"}
                        >
                          {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                      <FieldError name="confirm" />
                    </div>
                  </div>

                  {/* INFORMATION */}
                  <div className="mt-6 flex items-start gap-3 rounded-lg border border-emerald-100 bg-emerald-50/60 p-4">
                    <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-700" />
                    <div>
                      <p className="text-xs font-bold text-emerald-900">
                        Secure Account Creation
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-emerald-800">
                        The password is sent to the authenticated backend over the configured connection and hashed using Argon2id before storage. Use HTTPS in production.
                      </p>
                    </div>
                  </div>
                </div>

                {/* FOOTER ACTIONS */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/60 px-5 py-4">
                  <p className="text-[11px] text-slate-500">
                    <span className="font-bold text-red-500">*</span> Required fields
                  </p>

                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={resetForm}
                      disabled={saving}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 disabled:opacity-50"
                    >
                      <X size={15} />
                      Clear
                    </button>

                    <Link
                      to={LIST_ROUTE}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 text-xs font-semibold text-slate-600 transition hover:bg-slate-100"
                    >
                      <ArrowLeft size={15} />
                      Cancel
                    </Link>

                    <button
                      type="submit"
                      disabled={saving}
                      className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-emerald-700 px-5 text-xs font-bold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {saving ? (
                        <>
                          <Loader2 size={15} className="animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save size={15} />
                          Save Supreme Admin
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </section>
            </form>

            {/* FOOTER */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 pt-4 text-[11px] text-slate-400">
              <span>Infra V5 Supreme Admin / Add User</span>
              <span>Global Administrator Account Management</span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
